import type { FunctionComponent } from "react";
import { lazy, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { BootMark } from "~/components/BootMark.tsx";
import AppLayoutPlain from "~/components/layout/AppLayoutPlain.tsx";
import RootPage from "~/components/layout/RootPage.tsx";
import {
	ADMIN_PATH_WITH_TAB_ID,
	appRoutes,
	CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH,
	CENTRAL_INVOICE_RECIPIENTS_INDEX_PATH,
	CHANGELOG_PATH,
	COMPANY_DETAILS_PATH,
	HR_STAFF_BENEFITS_REPORT_PATH,
	HR_STAFF_MEMBER_DETAILS_PATH_WITH_TAB_ID,
	HR_STAFF_MEMBERS_INDEX_PATH,
	HR_STAFF_MONTHLY_REPORT_PATH,
	HR_STATISTICS_PATH,
	HR_TEAM_OVERVIEW_PATH,
	INVOICE_DETAILS_PATH,
	INVOICES_INDEX_PATH,
	INVOICES_PENDING_PATH,
	PROJECT_DESCRIPTIONS_INDEX_PATH,
	PROJECT_DETAILS_PATH_WITH_TAB_ID,
	QUOTA_DETAILS_PATH,
	QUOTAS_INDEX_PATH,
	REPORTS_PATH_WITH_TAB_ID,
	STAFF_MEMBER_ABSENCES_PATH,
	STAFF_MEMBER_VACATION_LEDGER_PATH,
	STAFF_MEMBER_WORKING_SCHEDULES_PATH,
	TIME_TRACKINGS_PATH,
} from "~/constants/appRoutes.ts";
import { COMPENSATION_VISIBILITY_CHANNEL_NAME } from "~/constants/broadcastChannelNames.ts";
import { APP_MODE } from "~/constants/envConstants.ts";
import { mainPopUpPortalId } from "~/constants/portalIds.ts";
import { useAuth } from "~/contexts/AuthContext";
import useBroadcastChannel from "~/hooks/useBroadcastChannel";
import { DashboardPage } from "~/pages/dashboard/DashboardPage.tsx";
import { LandingPage } from "~/pages/LandingPage";
import ProtectedRoutes from "~/pages/mainRouter/ProtectedRoutes";
import { setIntendedLocation } from "~/services/intendedLocation";
import { useStaffMemberCompensationVisibilityStore } from "~/stores/useStaffMemberCompensationVisibilityStore.ts";
import { PermissionNames } from "~/types/entityNames.ts";


/*
	The dashboard, the app shell and LandingPage stay eager. LandingPage only redirects to the dashboard, so a lazy chunk
	would add a request before the dashboard. The first chunk of a reload on the dashboard stays small. Every other
	page loads in its own chunk when its route opens. AppLayoutPlain and RootPage hold the Suspense fallback.
*/
const AbsenceCalendarPage = lazy(() => import("~/pages/absence/AbsenceCalendarPage"));
const AbsencesPage = lazy(() => import("~/pages/absence/AbsencesPage"));
const AdminPage = lazy(() => import("~/pages/admin/AdminPage"));
const CentralInvoiceRecipientDetailsPage = lazy(() => import("~/pages/client/CentralInvoiceRecipientDetailsPage.tsx"));
const CentralInvoiceRecipientsIndexPage = lazy(() => import("~/pages/client/CentralInvoiceRecipientsIndexPage.tsx"));
const ChangelogPage = lazy(() => import("~/pages/changelog/ChangelogPage"));
const ClientDetailsPage = lazy(() => import("~/pages/client/ClientDetailsPage.tsx"));
const ClientsIndexPage = lazy(() => import("~/pages/client/ClientsIndexPage.tsx"));
const CompanyDetailsPage = lazy(() => import("~/pages/client/CompanyDetailsPage.tsx"));
const ContactDetailsPage = lazy(() => import("~/pages/contact/ContactDetailsPage.tsx"));
const ContactsIndexPage = lazy(() => import("~/pages/contact/ContactsIndexPage.tsx"));
const DevPlayground = lazy(() => import("~/pages/DevPlayground"));
const Error404Page = lazy(() => import("~/pages/Error404Page.tsx"));
const GlobalReportsPage = lazy(() => import("~/pages/reports/GlobalReportsPage"));
const InvoiceDetailsPage = lazy(() => import("~/pages/billing/InvoiceDetailsPage"));
const InvoicesIndexPage = lazy(() => import("~/pages/billing/InvoicesIndexPage.tsx"));
const LoginPage = lazy(() => import("~/pages/login/LoginPage.tsx"));
const LogoutPage = lazy(() => import("~/pages/logout/LogoutPage"));
const PendingInvoicesIndexPage = lazy(() => import("~/pages/billing/PendingInvoicesIndexPage.tsx"));
const ProjectDetailsPage = lazy(() => import("~/pages/project/ProjectDetailsPage.tsx"));
const ProjectReferenceTextsSearchPage = lazy(() => import("~/pages/project/ProjectReferenceTextsSearchPage.tsx"));
const ProjectsIndexPage = lazy(() => import("~/pages/project/ProjectsIndexPage.tsx"));
const QuotaDetailsPage = lazy(() => import("~/pages/quota/QuotaDetailsPage.tsx"));
const QuotasIndexPage = lazy(() => import("~/pages/quota/QuotasIndexPage.tsx"));
const RequestPasswordReset = lazy(() => import("~/pages/users/RequestPasswordReset"));
const ResetPassword = lazy(() => import("~/pages/users/ResetPassword"));
const StaffBenefitsReportPage = lazy(() => import("~/pages/humanResources/StaffBenefitsReportPage"));
const StaffMemberDetailsPage = lazy(() => import("~/pages/humanResources/StaffMemberDetailsPage"));
const StaffMembersIndexPage = lazy(() => import("~/pages/humanResources/StaffMembersIndexPage"));
const StaffMonthlyReportPage = lazy(() => import("~/pages/humanResources/StaffMonthlyReportPage"));
const StaffStatisticsPage = lazy(() => import("~/pages/humanResources/StaffStatisticsPage"));
const StaffTeamOverviewPage = lazy(() => import("~/pages/humanResources/StaffTeamOvervIewPage"));
const TimeTrackingPage = lazy(() => import("~/pages/timetracking/TimeTrackingPage"));
const VacationLedgerPage = lazy(() => import("~/pages/absence/VacationLedgerPage"));
const WorkingSchedulesPage = lazy(() => import("~/pages/absence/WorkingSchedulesPage"));

const MainRouter: FunctionComponent = () => {
	const { user, isCheckingAuthStatus, hasAnyPermission } = useAuth();
	const location = useLocation();

	useEffect(() => {
		if (!isCheckingAuthStatus && !user && ![appRoutes.login().path].includes(location.pathname)) {
			setIntendedLocation();
		}
	}, [location.pathname, location.search, location.hash, user, isCheckingAuthStatus]);

	const setVisibility = useStaffMemberCompensationVisibilityStore((state) => state.setVisibility);

	const { start } = useIdleTimer({
		onIdle: () => {
			setVisibility(false);
		},
		startManually: true,
		events: ["keydown", "mousedown", "scroll"],
		timeout: 60 * 1000,
		throttle: 500,
		crossTab: true,
	});


	useEffect(() => {
		if (user && hasAnyPermission([PermissionNames.CanManagesStaffSalaries])) {
			start();
		}
	}, [user, setVisibility, hasAnyPermission, start]);

	useBroadcastChannel(COMPENSATION_VISIBILITY_CHANNEL_NAME, (e) => {
		if (e.data === "show") {
			setVisibility(true);
		}

		if (e.data === "hide") {
			setVisibility(false);
		}
	});

	return (
		<>
			{/*
				The auth check needs the user, and the navigation needs the user, so no layout renders yet. BootMark looks the
				same as the loader in index.html, so the change from HTML to React does not flash.
			*/}
			{isCheckingAuthStatus && <BootMark />}
			{!isCheckingAuthStatus && (
				<Routes>
					<Route element={<AppLayoutPlain />}>
						<Route element={<ProtectedRoutes />}>
							<Route path=""
								   element={<LandingPage />} />
							<Route path={appRoutes.dashboard().path}
								   element={<DashboardPage />} />
							<Route path={appRoutes.absenceOverview().path}
								   element={<AbsenceCalendarPage />} />
							<Route path={CHANGELOG_PATH}
								   element={<ChangelogPage />} />
							<Route path={appRoutes.clients().path}
								   element={<ClientsIndexPage />} />
							<Route path={appRoutes.client().path}
								   element={<ClientDetailsPage />} />
							<Route path={CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH}
								   element={<CentralInvoiceRecipientDetailsPage />} />
							<Route path={CENTRAL_INVOICE_RECIPIENTS_INDEX_PATH}
								   element={<CentralInvoiceRecipientsIndexPage />} />
							<Route path={appRoutes.contacts().path}
								   element={<ContactsIndexPage />} />
							<Route path={appRoutes.contact().path}
								   element={<ContactDetailsPage />} />
							<Route path={appRoutes.projects().path}
								   element={<ProjectsIndexPage />} />
							<Route path={COMPANY_DETAILS_PATH}
								   element={<CompanyDetailsPage />} />
							<Route path={INVOICE_DETAILS_PATH}
								   element={<InvoiceDetailsPage />} />
							<Route path={INVOICES_PENDING_PATH}
								   element={<PendingInvoicesIndexPage />} />
							<Route path={INVOICES_INDEX_PATH}
								   element={<InvoicesIndexPage />} />
							<Route path={PROJECT_DESCRIPTIONS_INDEX_PATH}
								   element={<ProjectReferenceTextsSearchPage />} />
							<Route path={PROJECT_DETAILS_PATH_WITH_TAB_ID}
								   element={<ProjectDetailsPage />} />
							<Route path={STAFF_MEMBER_ABSENCES_PATH}
								   element={<AbsencesPage />} />
							<Route path={STAFF_MEMBER_VACATION_LEDGER_PATH}
								   element={<VacationLedgerPage />} />
							<Route path={STAFF_MEMBER_WORKING_SCHEDULES_PATH}
								   element={<WorkingSchedulesPage />} />
							<Route path={HR_STAFF_BENEFITS_REPORT_PATH}
								   element={<StaffBenefitsReportPage />} />
							<Route path={HR_STAFF_MEMBERS_INDEX_PATH}
								   element={<StaffMembersIndexPage />} />
							<Route path={HR_STAFF_MONTHLY_REPORT_PATH}
								   element={<StaffMonthlyReportPage />} />
							<Route path={HR_STATISTICS_PATH}
								   element={<StaffStatisticsPage />} />
							<Route path={HR_TEAM_OVERVIEW_PATH}
								   element={<StaffTeamOverviewPage />} />
							<Route path={HR_STAFF_MEMBER_DETAILS_PATH_WITH_TAB_ID}
								   element={<StaffMemberDetailsPage />} />
							<Route path={QUOTA_DETAILS_PATH}
								   element={<QuotaDetailsPage />} />
							<Route path={appRoutes.logout().path}
								   element={<LogoutPage />} />
							<Route path={TIME_TRACKINGS_PATH}
								   element={<TimeTrackingPage />} />
							<Route path={REPORTS_PATH_WITH_TAB_ID}
								   element={<GlobalReportsPage />} />
							<Route path={TIME_TRACKINGS_PATH}
								   element={<TimeTrackingPage />} />
							<Route path={ADMIN_PATH_WITH_TAB_ID}
								   element={<AdminPage />} />
							<Route path={QUOTAS_INDEX_PATH}
								   element={<QuotasIndexPage/>} />
							{APP_MODE === "development" && <Route path={"test"}
																  element={<DevPlayground />} />}
							<Route path="*"
								   element={<Error404Page />} />
						</Route>
					</Route>
					<Route element={<RootPage />}>
						<Route path={appRoutes.passwordReset().path}
							   element={<ResetPassword />} />
						<Route path={appRoutes.passwordResetRequest().path}
							   element={<RequestPasswordReset />} />
						<Route path={appRoutes.login().path}
							   element={<LoginPage />} />
						<Route path={"*"}
							   element={<Navigate to={appRoutes.login().path} />} />
					</Route>
				</Routes>
			)}
			<div id={mainPopUpPortalId} />
		</>
	);

};

export default MainRouter;
